import type { ConfettiShape } from "@/interfaces/redemption";

/**
 * Campaign-builder Landing Page tab constants. The landing preview reuses the
 * shipped recipient-flow components (RedemptionHero / RedemptionCollection / …),
 * so these are the builder-only config defaults + the Pro (plan-gated) Confetti
 * and Custom Colors data. Ported from the master prototype's landing config panel.
 */

// Hero copy — the shipped RedemptionHero's hardcoded defaults, now editable.
export const DEFAULT_LANDING_HEADLINE = "Your Gift that Gives Back";
export const DEFAULT_LANDING_SUBTITLE = "Every gift in this collection helps people or the planet.";

export const LANDING_FIELD_LIMITS = {
  headline: 60,
  subtitle: 320,
} as const;

/**
 * Custom Colors defaults (Pro). `heroBg` is the clear-dust hero surface, `accent`
 * is the dust brand token (`--color-dark-dust` = #af9577) applied by recoloring
 * that CSS variable on the preview wrapper, and `envelope` is the gift-message
 * envelope color (also the recipient flow's `campaigns.envelope_color`).
 */
export const LANDING_COLOR_DEFAULTS = {
  heroBg: "#FBF9F7",
  envelope: "#333333",
  accent: "#AF9577",
} as const;

export type HeroTextMode = "dark" | "light";

/** A selectable confetti particle shape (Pro Confetti control). */
export interface ConfettiShapeOption {
  id: ConfettiShape;
  name: string;
  /** Material icon name. */
  icon: string;
}

export const CONFETTI_SHAPES: ConfettiShapeOption[] = [
  { id: "mixed", name: "Mixed", icon: "auto_awesome" },
  { id: "teardrop", name: "Teardrop", icon: "water_drop" },
  { id: "heart", name: "Heart", icon: "favorite" },
  { id: "star", name: "Star", icon: "star" },
  { id: "rect", name: "Rectangle", icon: "rectangle" },
  { id: "circle", name: "Circle", icon: "circle" },
  { id: "oval", name: "Oval", icon: "lens" },
];

/** A named 6-color confetti scheme (Pro). `null` scheme ⇒ the default brand foil. */
export interface ConfettiScheme {
  name: string;
  colors: string[];
}

export const CONFETTI_SCHEMES: ConfettiScheme[] = [
  {
    name: "Corporate Blue",
    colors: ["#002D72", "#0057B8", "#4A90D9", "#A8C8F0", "#C0C8D8", "#E8F2FB"],
  },
  {
    name: "Classic Rainbow",
    colors: ["#FF3B30", "#FF9500", "#FFCC00", "#34C759", "#007AFF", "#AF52DE"],
  },
  {
    name: "Warm & Festive",
    colors: ["#C1121F", "#E85D04", "#F77F00", "#FCBF49", "#FFE169", "#FFF3B0"],
  },
  {
    name: "Earth & Organic",
    colors: ["#2D6A4F", "#52B788", "#6B4423", "#A98467", "#D4A373", "#E9EDC9"],
  },
  {
    name: "Corporate Green",
    colors: ["#00361A", "#006837", "#2D9E5F", "#72C49A", "#B2DEC5", "#E3F5EC"],
  },
  {
    name: "Love Struck",
    colors: ["#6B0F1A", "#B5172B", "#E03E52", "#F4909A", "#F9C4CA", "#FDEAEC"],
  },
  {
    name: "Pastel & Soft",
    colors: ["#FFB3C1", "#FFC8A2", "#FDFFB6", "#CAFFBF", "#BDE0FE", "#D4B8E0"],
  },
  { name: "Cool Tech", colors: ["#6C63FF", "#3A0CA3", "#4361EE", "#4CC9F0", "#F72585", "#7209B7"] },
  {
    name: "Gold & Champagne",
    colors: ["#B8860B", "#C9A227", "#D4AF37", "#E8C95D", "#F5E195", "#FFF8DC"],
  },
  {
    name: "Monochrome Bold",
    colors: ["#1A1A1A", "#3D3D3D", "#6B6B6B", "#AAAAAA", "#D6D6D6", "#D4AF37"],
  },
];

/**
 * The flat color scheme for a selected scheme index, or `undefined` (⇒ the default
 * brand foil palette) when no scheme is chosen. Fed to `<Confetti colors=… />`.
 */
export function resolveSchemeColors(schemeIndex: number | null): string[] | undefined {
  if (schemeIndex === null) return undefined;
  return CONFETTI_SCHEMES[schemeIndex]?.colors;
}

/**
 * A beneficiary cause the sender pledges unclaimed gift value to — chosen in step 4
 * (charity selection). Structurally a superset of the recipient pledge banner's
 * `beneficiary` shape, so a cause can be passed straight to RedemptionPledgeBanner.
 * `impactLine` is the charity's short impact sentence (Impact Desc (Short),
 * `impact.description_short`), shown verbatim as a full sentence and hidden
 * when blank; images fall back to icons when absent.
 */
export interface BeneficiaryCause {
  id: string;
  name: string;
  impactLine?: string;
  imageUrl?: string;
  impactIconUrl?: string;
  about?: string;
}
